import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageLightbox from "@/components/ImageLightbox";
import { AlertsDialog } from "@/components/FuelReceipts";
import {
  PhotoField, WhenField, MethodChips, toNYInput, fromNYInput, nyText, money,
  methodLabels, thumbUrl, fieldInput,
} from "@/components/ReceiptBits";
import { Plus, FileWarning, Loader2, Trash2, Bell, Search, Check } from "lucide-react";

/**
 * My Dashboard → Tickets. A unit who got a ticket on a call submits it (photo,
 * when, amount, the incident) and sees their own; tickets editors see
 * everyone's and mark them reimbursed.
 */

const label = "text-xs font-medium text-slate-600 dark:text-slate-300 mb-1";

const incidentText = (i) =>
  i ? [i.incident_id, i.incident_type || i.headline, i.address].filter(Boolean).join(" · ") : "";

export default function Tickets({ user }) {
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({ tickets: [], owed: 0, open_count: 0, editor: false });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tickets`, { params: { scope, status }, withCredentials: true });
      setData(r.data);
    } catch {
      toast.error("Couldn't load tickets");
    } finally {
      setLoading(false);
    }
  }, [scope, status]);

  useEffect(() => { load(); }, [load]);

  const editor = data.editor;
  const seg = (opts, value, set) => (
    <div className="inline-flex rounded-md overflow-hidden border border-slate-300 dark:border-slate-700">
      {opts.map(([k, l]) => (
        <button key={k} type="button" onClick={() => set(k)}
          className={`px-3 h-9 text-sm font-medium ${value === k ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {editor && seg([["mine", "Mine"], ["all", "Everyone"]], scope, setScope)}
        {seg([["all", "All"], ["open", "Not reimbursed"], ["reimbursed", "Reimbursed"]], status, setStatus)}
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {scope === "all" ? "Owed" : "Owed to you"}: <b className="text-amber-600 dark:text-amber-400">{money(data.owed)}</b>
        </span>
        <div className="ml-auto flex gap-2">
          {editor && (
            <button type="button" onClick={() => setAlertsOpen(true)} title="Who gets told about new tickets"
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              <Bell className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={() => setAdding(true)}
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            <Plus className="w-4 h-4" />Submit ticket
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : data.tickets.length === 0 ? (
        <div className="py-12 text-center text-slate-500">
          <FileWarning className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No tickets
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.tickets.map((t) => (
            <button key={t.ticket_id} type="button" onClick={() => setOpen(t)}
              className="text-left flex gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 hover:border-slate-400 dark:hover:border-slate-600">
              <img src={thumbUrl(t.photo_url, 200)} alt="" className="w-16 h-16 shrink-0 rounded object-cover bg-slate-100 dark:bg-slate-800" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-slate-900 dark:text-white truncate">
                    {scope === "all" ? `${t.submitted_by_name}${t.submitted_unit ? ` (${t.submitted_unit})` : ""}` : nyText(t.issued_at)}
                  </span>
                  <span className="font-bold text-slate-900 dark:text-white tabular-nums">{money(t.amount)}</span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {scope === "all" ? `${nyText(t.issued_at)} · ` : ""}{incidentText(t.incident)}
                </div>
                <div className="mt-1">
                  {t.reimbursed ? (
                    <span className="inline-block rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold px-2 py-0.5">
                      Reimbursed {money(t.reimbursed_amount)} · {methodLabels(t.reimbursed_methods)} · {nyText(t.reimbursed_at, false)}
                    </span>
                  ) : (
                    <span className="inline-block rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[11px] font-semibold px-2 py-0.5">
                      Not reimbursed
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {adding && (
        <TicketForm
          user={user}
          initial={{ when: toNYInput(), amount: "", note: "", ticket_number: "", photo_url: "", incident: null }}
          onOpenPhoto={setPhoto}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}
      {open && (
        <TicketDetail
          ticket={open}
          user={user}
          editor={editor}
          onOpenPhoto={setPhoto}
          onClose={() => setOpen(null)}
          onChanged={(t) => { if (t) setOpen(t); else setOpen(null); load(); }}
        />
      )}
      {alertsOpen && <AlertsDialog endpoint="/ticket-alerts" what="ticket" onClose={() => setAlertsOpen(false)} />}
      {photo && <ImageLightbox src={`${photo}/thumb?size=2400`} onClose={() => setPhoto(null)} />}
    </div>
  );
}

/** The incident the ticket belongs to: your unit's incidents (editors: any), searchable. */
function IncidentPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [noUnit, setNoUnit] = useState(false);
  const [editor, setEditor] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/tickets/incidents`, { params: { search: q }, withCredentials: true })
        .then((r) => { setRows(r.data.incidents || []); setNoUnit(!!r.data.no_unit); setEditor(!!r.data.editor); })
        .catch(() => setRows([]));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className={label}>Incident you were responding to</div>
      {value && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-blue-300 dark:border-blue-500/50 bg-blue-50 dark:bg-blue-500/10 px-3 py-2 text-sm text-slate-900 dark:text-slate-100">
          <Check className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
          <span className="min-w-0 break-words">{incidentText(value)}</span>
        </div>
      )}
      {noUnit ? (
        <p className="text-sm text-amber-700 dark:text-amber-400">Your account isn't linked to a unit, so there are no incidents to pick. Ask an admin.</p>
      ) : (
        <>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={editor ? "Search any incident…" : "Search your unit's incidents…"}
              className={`${fieldInput} pl-9`} />
          </div>
          <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
            {rows === null ? (
              <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : rows.length === 0 ? (
              <div className="py-3 text-center text-xs text-slate-500">No incidents found</div>
            ) : rows.map((i) => (
              <button key={i.incident_id} type="button" onClick={() => onChange(i)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${value?.incident_id === i.incident_id ? "bg-blue-50 dark:bg-blue-500/10" : ""}`}>
                <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{i.incident_id} · {i.incident_type || i.headline}</div>
                <div className="text-xs text-slate-500 truncate">{nyText(i.date_time)}{i.address ? ` · ${i.address}` : ""}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TicketForm({ user, initial, ticket, onOpenPhoto, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.photo_url) return toast.error("Add a photo of the ticket");
    if (!f.incident) return toast.error("Pick the incident");
    if (!(parseFloat(f.amount) > 0)) return toast.error("Enter the amount");
    setSaving(true);
    const body = {
      photo_url: f.photo_url, issued_at: fromNYInput(f.when), amount: parseFloat(f.amount),
      incident_id: f.incident.incident_id, ticket_number: f.ticket_number, note: f.note,
    };
    try {
      const r = ticket
        ? await axios.put(`${API}/tickets/${ticket.ticket_id}`, body, { withCredentials: true })
        : await axios.post(`${API}/tickets`, body, { withCredentials: true });
      toast.success(ticket ? "Ticket updated" : "Ticket submitted");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileWarning className="w-5 h-5" />{ticket ? "Edit ticket" : "Submit a ticket"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <PhotoField label="Ticket photo" value={f.photo_url} onChange={set("photo_url")} onOpen={onOpenPhoto} />
          <WhenField label="Issued" value={f.when} onChange={set("when")} />
          <div>
            <div className={label}>Amount</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <input type="text" inputMode="decimal" placeholder="0.00" value={f.amount}
                onChange={(e) => set("amount")(e.target.value.replace(/[^0-9.]/g, ""))} className={`${fieldInput} pl-7`} />
            </div>
          </div>
          <IncidentPicker value={f.incident} onChange={set("incident")} />
          <div>
            <div className={label}>Ticket number (optional)</div>
            <input value={f.ticket_number} onChange={(e) => set("ticket_number")(e.target.value)} className={fieldInput} />
          </div>
          <div>
            <div className={label}>Submitted by</div>
            <div className="text-sm text-slate-900 dark:text-slate-100">
              {ticket ? ticket.submitted_by_name : user?.name}
              {(ticket ? ticket.submitted_unit : user?.linked_unit) ? ` (${ticket ? ticket.submitted_unit : user?.linked_unit})` : ""}
            </div>
          </div>
          <div>
            <div className={label}>Note (optional)</div>
            <textarea rows={2} value={f.note} onChange={(e) => set("note")(e.target.value)} className={`${fieldInput} h-auto py-2`} />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200">Cancel</button>
            <button type="button" disabled={saving} onClick={save} className="flex-1 h-10 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}{ticket ? "Save" : "Submit"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TicketDetail({ ticket: t, user, editor, onOpenPhoto, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [methods, setMethods] = useState(t.reimbursed_methods || []);
  const [amount, setAmount] = useState(String(t.reimbursed_amount ?? t.amount ?? ""));
  const [paidOn, setPaidOn] = useState(toNYInput());
  const [note, setNote] = useState(t.reimbursement_note || "");
  const [busy, setBusy] = useState(false);
  const canChange = editor || !t.reimbursed;

  const reimburse = async (yes) => {
    if (yes && methods.length === 0) return toast.error("Pick how it was paid");
    if (yes && !(parseFloat(amount) > 0)) return toast.error("Enter the amount paid back");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/tickets/${t.ticket_id}/reimbursement`,
        { reimbursed: yes, methods, amount: parseFloat(amount), reimbursed_at: fromNYInput(paidOn), note },
        { withCredentials: true });
      toast.success(yes ? "Marked reimbursed" : "Marked not reimbursed");
      onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    try {
      await axios.delete(`${API}/tickets/${t.ticket_id}`, { withCredentials: true });
      toast.success("Ticket deleted");
      onChanged(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  if (editing) {
    return (
      <TicketForm
        user={user}
        ticket={t}
        initial={{ when: toNYInput(new Date(t.issued_at)), amount: String(t.amount), note: t.note || "",
                   ticket_number: t.ticket_number || "", photo_url: t.photo_url, incident: t.incident }}
        onOpenPhoto={onOpenPhoto}
        onClose={() => setEditing(false)}
        onSaved={(nt) => { setEditing(false); onChanged(nt); }}
      />
    );
  }

  return (
    <>
      <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileWarning className="w-5 h-5" />Ticket · {money(t.amount)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <button type="button" onClick={() => onOpenPhoto(t.photo_url)} className="block">
              <img src={thumbUrl(t.photo_url, 600)} alt="Ticket" className="max-h-56 rounded-md border border-slate-200 dark:border-slate-700 object-contain bg-slate-50 dark:bg-slate-950" />
            </button>
            <div className="grid grid-cols-[6.5rem_1fr] gap-y-1 text-slate-700 dark:text-slate-200">
              <span className="text-slate-500">Issued</span><span>{nyText(t.issued_at)}</span>
              <span className="text-slate-500">Incident</span><span className="break-words">{incidentText(t.incident)}</span>
              {t.ticket_number && (<><span className="text-slate-500">Ticket #</span><span>{t.ticket_number}</span></>)}
              <span className="text-slate-500">Submitted by</span><span>{t.submitted_by_name}{t.submitted_unit ? ` (${t.submitted_unit})` : ""}</span>
              {t.note && (<><span className="text-slate-500">Note</span><span className="break-words">{t.note}</span></>)}
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              {t.reimbursed ? (
                <>
                  <div className="font-semibold text-emerald-700 dark:text-emerald-400">Reimbursed {money(t.reimbursed_amount)}</div>
                  <div className="text-slate-700 dark:text-slate-200">
                    {methodLabels(t.reimbursed_methods)} · {nyText(t.reimbursed_at, false)} · by {t.reimbursed_by_name}
                  </div>
                  {t.reimbursement_note && <div className="text-slate-500 break-words">{t.reimbursement_note}</div>}
                  {editor && (
                    <button type="button" disabled={busy} onClick={() => reimburse(false)}
                      className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200">
                      Mark not reimbursed
                    </button>
                  )}
                </>
              ) : editor ? (
                <>
                  <div className="font-semibold text-slate-900 dark:text-white">Mark reimbursed to {t.submitted_by_name}</div>
                  <div className={label}>How</div>
                  <MethodChips value={methods} onChange={setMethods} />
                  <div>
                    <div className={label}>Amount paid back</div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input type="text" inputMode="decimal" value={amount}
                        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} className={`${fieldInput} pl-7`} />
                    </div>
                  </div>
                  <WhenField label="Paid on" value={paidOn} onChange={setPaidOn} />
                  <div>
                    <div className={label}>Note (optional)</div>
                    <input value={note} onChange={(e) => setNote(e.target.value)} className={fieldInput} />
                  </div>
                  <div className="text-xs text-slate-500">Paid by: {user?.name}</div>
                  <button type="button" disabled={busy} onClick={() => reimburse(true)}
                    className="w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}Mark reimbursed
                  </button>
                </>
              ) : (
                <div className="font-semibold text-amber-700 dark:text-amber-400">Not reimbursed yet</div>
              )}
            </div>

            {canChange && (
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(true)}
                  className="flex-1 h-10 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200">
                  Edit
                </button>
                <button type="button" onClick={() => setConfirmDel(true)}
                  className="h-10 px-3 rounded-md border border-red-300 dark:border-red-500/50 text-sm font-medium text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                  <Trash2 className="w-4 h-4" />Delete
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
            <AlertDialogDescription>{money(t.amount)} from {t.submitted_by_name}. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
